import './task-card.scss';
import moment from 'moment';
import {Avatar, ButtonBase, Grid, Stack, SxProps, Typography, Theme, Chip} from '@mui/material';
import {AutonomyChip} from '../../shared/badges/autonomy-chip';
import {TaskStatus} from '../../shared/badges/task-status';
import {CareTaskComplementary} from '../../../backend/interfaces/care-task-complementary';
import {EntityUser} from '../../../backend/responses/entity-user';
import {InteractionEvent, Pressable} from '../pressable';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';

interface TaskCardHeaderProps {
  displayType: 'user' | 'task';
  senior?: EntityUser;
  avatarBorderColor?: string;
  hideAvatar?: boolean;
  taskDetails?: CareTaskComplementary;
  taskInformationHeader?: boolean;
  isExpanded: boolean;
  sos: boolean;
  box: boolean;
  onClick?: (event: InteractionEvent) => void;
  onLongPress?: (event: InteractionEvent) => void;
  onSeniorAction?: (senior: EntityUser) => void;
  seniorActionsOpen?: boolean;
  seniorActionsDisabled?: boolean;
}

export default function TaskCardHeader(props: TaskCardHeaderProps) {
  if (props.onLongPress) {
    return (
      <Pressable onLongPress={props.onLongPress} onClick={props.onLongPress}>
        <Header {...props} />
      </Pressable>
    );
  }

  return <Header {...props} />;
}

const Header = ({
  displayType,
  senior,
  avatarBorderColor,
  hideAvatar,
  taskDetails,
  taskInformationHeader,
  isExpanded,
  sos,
  box,
  onClick,
  onSeniorAction,
  seniorActionsOpen = false,
  seniorActionsDisabled = false,
}: TaskCardHeaderProps) => {
  const {t} = useTranslation();

  const textSx: SxProps<Theme> = {
    fontSize: '16px',
    fontWeight: 400,
    letterSpacing: '0px',
    textAlign: 'left',
    color: 'var(--dcm-gray)',
    height: '25px',
    maxWidth: {xs: 'calc(100vw - 220px)', sm: '100%'},
    userSelect: 'none',
  };
  const boldTextSx: SxProps<Theme> = {
    fontWeight: 500,
  };
  const CardTitle = () => {
    if (displayType === 'user') {
      return (
        <Typography sx={{...textSx, ...boldTextSx}} noWrap>
          {senior?.user.displayName ?? senior?.user.name}
        </Typography>
      );
    } else {
      return (
        <Typography sx={textSx} noWrap>
          {moment(taskDetails?.careTask.startsAt).format('H:mm')}
          {(taskDetails?.careTask.nameConfigurationValue || taskDetails?.activityTypeName) && (
            <Typography sx={boldTextSx} component="span" noWrap>
              &nbsp;-&nbsp;
              {taskDetails?.careTask.nameConfigurationValue || taskDetails?.activityTypeName}
            </Typography>
          )}
        </Typography>
      );
    }
  };

  const seniorHeaderContent = (
    <>
      {!hideAvatar && (
        <Avatar
          sx={{
            height: '36px',
            width: '36px',
            borderRadius: '100%',
            border: `1.5px solid ${avatarBorderColor || 'transparent'}`,
          }}
          src={senior?.user.picture}
          alt={senior?.user.name}
        >
          {senior?.user.name.charAt(0).toUpperCase()}
        </Avatar>
      )}
      <Stack className="container">
        <Grid container direction="row" justifyContent="flex-start" alignItems="center">
          <CardTitle />
        </Grid>
        <Stack direction="row" alignItems="center" gap="6px">
          {!hideAvatar && displayType === 'task' && (
            <Typography sx={{...textSx, fontSize: '14px', height: 'inherit'}} noWrap>
              {senior?.user.displayName ?? senior?.user.name}
            </Typography>
          )}
          {taskDetails && <AutonomyChip task={taskDetails} />}
        </Stack>
        <Grid container direction="row">
          {box && isExpanded && <Chip className="box-chip" label="BOX" />}
          {sos && isExpanded && <Chip className="sos-chip" label="SOS" />}
        </Grid>
      </Stack>
    </>
  );

  return taskInformationHeader ? (
    <Stack
      flexDirection="row"
      alignContent="center"
      alignItems="center"
      justifyContent="space-between"
      gap="2"
      sx={{width: '100%'}}
    >
      <Typography
        sx={{
          fontSize: '18px',
          fontWeight: '500',
          lineHeight: '18px',
          letterSpacing: '0em',
          textAlign: 'left',
          color: 'var(--dcm-gray)',
          maxWidth: '80%',
        }}
        noWrap
      >
        {t(translations.care_and_activities.handle_task_modal.details)}
      </Typography>

      {taskDetails && (
        <TaskStatus pendingCareTaskId={taskDetails.careTask.pendingCareTaskId} task={taskDetails} />
      )}
    </Stack>
  ) : (
    <Stack
      flexDirection="row"
      justifyContent="space-between"
      gap="2"
      sx={{
        opacity: taskDetails?.careTask.markedAsHandled ? 0.5 : 1,
      }}
      onClick={onClick}
    >
      {senior &&
        (onSeniorAction && displayType === 'user' ? (
          <ButtonBase
            component="button"
            type="button"
            aria-label={t(translations.daily_care_log.senior_actions.open_actions, {
              name: senior.user.displayName ?? senior.user.name,
            })}
            aria-haspopup="dialog"
            aria-expanded={seniorActionsOpen}
            disabled={seniorActionsDisabled}
            onClick={(event) => {
              event.stopPropagation();
              onSeniorAction(senior);
            }}
            className="task-header"
            sx={{
              gap: 2,
              alignItems: 'center',
              justifyContent: 'flex-start',
              minWidth: 0,
              px: 0.5,
              mx: -0.5,
              borderRadius: 1,
              textAlign: 'left',
              '&.Mui-focusVisible': {
                outline: '2px solid var(--dcm-blue-base)',
                outlineOffset: 2,
              },
              '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {backgroundColor: 'rgba(0, 0, 0, 0.04)'},
              },
            }}
          >
            {seniorHeaderContent}
          </ButtonBase>
        ) : (
          <Stack className="task-header" gap={2} flexDirection="row" alignItems="center">
            {seniorHeaderContent}
          </Stack>
        ))}
      <Stack justifyContent="center">
        {taskDetails && <TaskStatus task={taskDetails} min={window.innerWidth <= 430} />}
      </Stack>
    </Stack>
  );
};
